import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Building2Icon,
  CheckCircle2Icon,
  Clock3Icon,
  MailCheckIcon,
  RefreshCwIcon,
  ShieldAlertIcon,
  UserIcon,
  UsersIcon,
  XCircleIcon,
} from "lucide-react";

import { supabase } from "../../lib/supabaseClient";

const STATUS_LABELS = {
  pending: "Väntar",
  approved: "Godkänd",
  rejected: "Avslagen",
  archived: "Arkiverad",
};

const USAGE_LABELS = {
  single_event: "En kommande tävling",
  few_per_year: "Några tävlingar per år",
  monthly: "Ungefär varje månad",
  club_operations: "Löpande klubbverksamhet",
  other: "Annat upplägg",
};

const statusClasses = {
  pending: "border-[#dfcfab] bg-[#fbf5e7] text-[#8a6623]",
  approved: "border-[#bfd7c7] bg-[#edf6f0] text-[#287148]",
  rejected: "border-[#dfc5c0] bg-[#fff4f1] text-[#a0443e]",
  archived: "border-[#d6d2c9] bg-[#f3f1eb] text-[#68716c]",
};

const getFunctionErrorMessage = async (error, fallback) => {
  if (!error) return fallback;

  try {
    if (error.context?.json) {
      const payload = await error.context.json();
      return payload?.error || payload?.message || fallback;
    }
  } catch (parseError) {
    console.warn("Kunde inte läsa Edge Function-felet:", parseError);
  }

  return error.message || fallback;
};

const formatDate = (value) => {
  if (!value) return "–";

  return new Intl.DateTimeFormat("sv-SE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

export default function BetaRequestsAdmin({ onChanged }) {
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [activeStatus, setActiveStatus] = useState("pending");
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const loadRequests = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const { data, error } = await supabase.functions.invoke(
        "manage-beta-access",
        {
          body: {
            action: "list",
            status: activeStatus === "all" ? "" : activeStatus,
          },
        }
      );

      if (error) {
        const message = await getFunctionErrorMessage(
          error,
          "Betaansökningarna kunde inte hämtas."
        );

        setErrorMessage(message);
        setRequests([]);
        return;
      }

      if (!data?.success) {
        setErrorMessage(
          data?.error || "Betaansökningarna kunde inte hämtas."
        );
        setRequests([]);
        return;
      }

      const nextRequests = Array.isArray(data.requests)
        ? data.requests
        : [];

      setRequests(nextRequests);

      setSelectedId((current) => {
        if (
          current &&
          nextRequests.some((item) => item.id === current)
        ) {
          return current;
        }

        return nextRequests[0]?.id || null;
      });
    } catch (error) {
      console.error("Kunde inte läsa betaansökningar:", error);
      setErrorMessage("Betaansökningarna kunde inte hämtas.");
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeStatus]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    setNote("");
    setSuccessMessage("");
  }, [selectedId]);

  const selectedRequest = useMemo(
    () =>
      requests.find((item) => item.id === selectedId) || null,
    [requests, selectedId]
  );

  const pendingCount = useMemo(
    () =>
      requests.filter((item) => item.status === "pending").length,
    [requests]
  );

  const runAction = async (action) => {
    if (!selectedRequest) return;

    const isApproval = action === "approve";

    const confirmed = window.confirm(
      isApproval
        ? `Godkänn ${selectedRequest.full_name} och skicka en personlig kod till ${selectedRequest.email}?`
        : `Markera ansökan från ${selectedRequest.full_name} som avslagen?`
    );

    if (!confirmed) return;

    setActionId(selectedRequest.id);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const { data, error } = await supabase.functions.invoke(
        "manage-beta-access",
        {
          body: {
            action,
            requestId: selectedRequest.id,
            note: note.trim(),
          },
        }
      );

      if (error) {
        const message = await getFunctionErrorMessage(
          error,
          isApproval
            ? "Ansökan kunde inte godkännas."
            : "Ansökan kunde inte avslås."
        );

        setErrorMessage(message);
        return;
      }

      if (!data?.success) {
        setErrorMessage(
          data?.error ||
            (isApproval
              ? "Ansökan kunde inte godkännas."
              : "Ansökan kunde inte avslås.")
        );
        return;
      }

      setSuccessMessage(
        data.message ||
          (isApproval
            ? "Ansökan godkändes och koden skickades."
            : "Ansökan markerades som avslagen.")
      );

      await loadRequests();
      await onChanged?.();
    } catch (error) {
      console.error("Administrativ åtgärd misslyckades:", error);
      setErrorMessage("Åtgärden kunde inte genomföras.");
    } finally {
      setActionId(null);
    }
  };

  return (
    <>
      <div className="border-b border-[#e4ded1] bg-[#fffdf8] px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {[
              ["pending", "Väntande"],
              ["approved", "Godkända"],
              ["rejected", "Avslagna"],
              ["all", "Alla"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setActiveStatus(value)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  activeStatus === value
                    ? "border-[#183d2d] bg-[#183d2d] text-white"
                    : "border-[#d3ccbf] bg-white text-[#68716c] hover:border-[#183d2d] hover:text-[#183d2d]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={loadRequests}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl border border-[#cfc7b8] bg-white px-3.5 py-2 text-xs font-semibold text-[#183d2d] transition hover:bg-[#f3efe6] disabled:opacity-50"
          >
            <RefreshCwIcon
              className={`h-4 w-4 ${
                isLoading ? "animate-spin" : ""
              }`}
            />
            Uppdatera
          </button>
        </div>
      </div>

      {(errorMessage || successMessage) && (
        <div className="px-4 pt-4 sm:px-6">
          {errorMessage && (
            <div className="flex items-start gap-3 rounded-xl border border-[#dfc5c0] bg-[#fff8f6] px-4 py-3 text-sm text-[#a0443e]">
              <ShieldAlertIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{errorMessage}</p>
            </div>
          )}

          {successMessage && (
            <div className="flex items-start gap-3 rounded-xl border border-[#bfd7c7] bg-[#edf6f0] px-4 py-3 text-sm text-[#287148]">
              <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{successMessage}</p>
            </div>
          )}
        </div>
      )}

      <div className="grid min-h-[620px] lg:grid-cols-[0.82fr_1.18fr]">
        <aside className="border-b border-[#e4ded1] bg-[#faf8f2] lg:border-b-0 lg:border-r">
          <div className="flex items-center justify-between border-b border-[#e4ded1] px-4 py-4 sm:px-5">
            <div>
              <p className="text-sm font-semibold text-[#183d2d]">
                {activeStatus === "pending"
                  ? "Väntande ansökningar"
                  : "Ansökningar"}
              </p>

              <p className="mt-0.5 text-xs text-[#7b817d]">
                {requests.length} resultat
              </p>
            </div>

            {activeStatus === "pending" && pendingCount > 0 && (
              <span className="rounded-full bg-[#b08b45] px-2.5 py-1 text-[10px] font-semibold text-white">
                {pendingCount}
              </span>
            )}
          </div>

          <div className="max-h-[620px] overflow-y-auto">
            {isLoading ? (
              <div className="p-6 text-center text-sm text-[#68716c]">
                Hämtar ansökningar…
              </div>
            ) : requests.length === 0 ? (
              <div className="p-8 text-center">
                <MailCheckIcon className="mx-auto h-8 w-8 text-[#a8aaa5]" />

                <p className="mt-3 text-sm font-semibold text-[#183d2d]">
                  Inga ansökningar här
                </p>

                <p className="mt-1 text-xs leading-5 text-[#7b817d]">
                  Nya ansökningar visas automatiskt när formuläret
                  används.
                </p>
              </div>
            ) : (
              requests.map((item) => {
                const isSelected = item.id === selectedId;

                const AccountIcon =
                  item.account_type === "club"
                    ? Building2Icon
                    : UsersIcon;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                    className={`w-full border-b border-[#e9e4da] px-4 py-4 text-left transition sm:px-5 ${
                      isSelected
                        ? "bg-[#e8f0ea]"
                        : "bg-transparent hover:bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#183d2d] shadow-sm">
                        <AccountIcon className="h-4 w-4" />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="flex items-start justify-between gap-3">
                          <span className="truncate text-sm font-semibold text-[#183d2d]">
                            {item.full_name}
                          </span>

                          <span
                            className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] ${
                              statusClasses[item.status] ||
                              statusClasses.archived
                            }`}
                          >
                            {STATUS_LABELS[item.status] ||
                              item.status}
                          </span>
                        </span>

                        <span className="mt-1 block truncate text-xs text-[#68716c]">
                          {item.email}
                        </span>

                        <span className="mt-2 flex items-center gap-1.5 text-[10px] text-[#969992]">
                          <Clock3Icon className="h-3 w-3" />
                          {formatDate(item.created_at)}
                        </span>
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <section className="p-4 sm:p-6 lg:p-8">
          {!selectedRequest ? (
            <div className="flex min-h-[420px] items-center justify-center text-center">
              <div>
                <UserIcon className="mx-auto h-9 w-9 text-[#b1b3ae]" />

                <p className="mt-3 text-sm font-semibold text-[#183d2d]">
                  Välj en ansökan
                </p>

                <p className="mt-1 text-xs text-[#7b817d]">
                  Detaljerna visas här.
                </p>
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl">
              <div className="flex flex-col gap-4 border-b border-[#e4ded1] pb-6 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9a7735]">
                    Ansökan
                  </p>

                  <h2 className="mt-2 text-2xl font-semibold text-[#183d2d]">
                    {selectedRequest.full_name}
                  </h2>

                  <p className="mt-1 text-sm text-[#68716c]">
                    {selectedRequest.email}
                  </p>
                </div>

                <span
                  className={`w-fit rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] ${
                    statusClasses[selectedRequest.status] ||
                    statusClasses.archived
                  }`}
                >
                  {STATUS_LABELS[selectedRequest.status] ||
                    selectedRequest.status}
                </span>
              </div>

              <div className="grid gap-4 py-6 sm:grid-cols-2">
                <InfoCard
                  label="Kontotyp"
                  value={
                    selectedRequest.account_type === "club"
                      ? "Golfklubb"
                      : "Privatperson / golfgäng"
                  }
                />

                <InfoCard
                  label="Planerad användning"
                  value={
                    USAGE_LABELS[selectedRequest.usage_frequency] ||
                    selectedRequest.usage_frequency
                  }
                />
              </div>

              <div className="space-y-5">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a8f8b]">
                    Klubb eller grupp
                  </p>

                  <p className="mt-2 text-sm text-[#1f2d26]">
                    {selectedRequest.organization || "Ej angivet"}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a8f8b]">
                    Motivering
                  </p>

                  <div className="mt-2 whitespace-pre-wrap rounded-2xl border border-[#e0d9cc] bg-white p-4 text-sm leading-7 text-[#4d5752]">
                    {selectedRequest.motivation}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="beta-admin-note"
                    className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a8f8b]"
                  >
                    Intern anteckning
                  </label>

                  <textarea
                    id="beta-admin-note"
                    rows={3}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Valfritt. Anteckningen skickas inte till den sökande."
                    className="mt-2 w-full resize-y rounded-xl border border-[#c9c1b2] bg-white px-3 py-3 text-sm leading-6 outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                  />
                </div>
              </div>

              <div className="mt-7 border-t border-[#e4ded1] pt-5">
                {selectedRequest.status === "pending" ? (
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => runAction("approve")}
                      disabled={actionId === selectedRequest.id}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(24,61,45,0.17)] transition hover:bg-[#214d39] disabled:opacity-50"
                    >
                      <MailCheckIcon className="h-4 w-4" />

                      {actionId === selectedRequest.id
                        ? "Behandlar…"
                        : "Godkänn och skicka kod"}
                    </button>

                    <button
                      type="button"
                      onClick={() => runAction("reject")}
                      disabled={actionId === selectedRequest.id}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#d8bdb7] bg-white px-5 py-3 text-sm font-semibold text-[#a0443e] transition hover:bg-[#fff5f3] disabled:opacity-50"
                    >
                      <XCircleIcon className="h-4 w-4" />
                      Avslå
                    </button>
                  </div>
                ) : (
                  <div className="rounded-xl border border-[#e0d9cc] bg-[#f8f5ed] px-4 py-3 text-xs leading-5 text-[#68716c]">
                    Ansökan behandlades{" "}
                    {formatDate(selectedRequest.reviewed_at)}.

                    {selectedRequest.internal_note && (
                      <span className="mt-1 block">
                        Anteckning:{" "}
                        {selectedRequest.internal_note}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-[#e0d9cc] bg-[#faf8f2] p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a8f8b]">
        {label}
      </p>

      <p className="mt-2 text-sm font-semibold text-[#183d2d]">
        {value || "–"}
      </p>
    </div>
  );
}